if (!customElements.get('ciari-custom-select')) {
  customElements.define('ciari-custom-select', class extends HTMLElement {
    connectedCallback() {
      this.nativeSelect = this.querySelector('[data-custom-select-native]');
      this.trigger = this.querySelector('[data-custom-select-trigger]');
      this.value = this.querySelector('[data-custom-select-value]');
      this.list = this.querySelector('[data-custom-select-list]');
      this.options = [...this.querySelectorAll('[data-custom-select-option]')];

      if (!this.nativeSelect || !this.trigger || !this.value || !this.list || !this.options.length) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.trigger.addEventListener('click', () => this.toggle(), { signal });
      this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event), { signal });
      this.list.addEventListener('click', (event) => this.onOptionClick(event), { signal });
      this.list.addEventListener('keydown', (event) => this.onListKeydown(event), { signal });
      this.nativeSelect.addEventListener('change', () => this.sync(), { signal });
      document.addEventListener('pointerdown', (event) => this.onDocumentPointerdown(event), { signal });
      this.addEventListener('focusout', (event) => this.onFocusout(event), { signal });
      this.nativeSelect.tabIndex = -1;
      this.nativeSelect.setAttribute('aria-hidden', 'true');
      this.sync();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.nativeSelect?.removeAttribute('tabindex');
      this.nativeSelect?.removeAttribute('aria-hidden');
    }

    get isOpen() {
      return this.trigger?.getAttribute('aria-expanded') === 'true';
    }

    get selectedOption() {
      return this.options.find((option) => option.dataset.value === this.nativeSelect.value) ?? this.options[0];
    }

    sync() {
      const selectedOption = this.selectedOption;
      if (!selectedOption) return;

      this.value.textContent = selectedOption.textContent.trim();
      for (const option of this.options) {
        option.setAttribute('aria-selected', String(option === selectedOption));
      }
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open(focus = true) {
      this.list.hidden = false;
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      if (focus) this.selectedOption?.focus();
    }

    close({ restoreFocus = false } = {}) {
      this.list.hidden = true;
      this.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.trigger.focus();
    }

    select(option) {
      const changed = this.nativeSelect.value !== option.dataset.value;
      this.nativeSelect.value = option.dataset.value;
      this.sync();
      this.close({ restoreFocus: true });

      if (changed) {
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onTriggerKeydown(event) {
      if (!['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      this.open();
    }

    onOptionClick(event) {
      const option = event.target.closest('[data-custom-select-option]');
      if (option) this.select(option);
    }

    onListKeydown(event) {
      const currentIndex = this.options.indexOf(document.activeElement);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + 1, this.options.length - 1);
      if (event.key === 'ArrowUp') nextIndex = Math.max(currentIndex - 1, 0);
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.options.length - 1;

      if (nextIndex !== currentIndex) {
        event.preventDefault();
        this.options[nextIndex].focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.select(document.activeElement);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close({ restoreFocus: true });
      } else if (event.key === 'Tab') {
        this.close();
      }
    }

    onDocumentPointerdown(event) {
      if (this.isOpen && !this.contains(event.target)) this.close();
    }

    onFocusout(event) {
      if (!this.isOpen || this.contains(event.relatedTarget)) return;

      requestAnimationFrame(() => {
        if (this.isOpen && !this.contains(document.activeElement)) this.close();
      });
    }
  });
}
